import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import api from "../../services/api";
import { toast } from "react-toastify";
import useWrapper from "../../hooks/useWrapper";
import useFormat from "../../hooks/utils/useFormat";
import { IntegerNumber } from "../Input";
import Button from "../Button";
import Slct from "../Slct";
import Results from "../Results";
import ModalCustom from "./modal-template-default";
import Required from "../Required";
import { validateForm } from "../ValidateForm";
import usePermission from "../../hooks/usePermission";

// Mesmo evento pode vir tanto do pacote quanto de um avulso do formando — evita
// duplicar a opção no Slct caso, por algum motivo, apareça nos dois.
function buildEventOptions(financial) {
  const _events = [...(financial?.package?.events || []), ...(financial?.singles?.map(__single => __single?.event) || [])];

  const _seen = new Set();
  return _events.filter(__event => {
    if (!__event) return false;
    if (__event.qtd_invitations == null && __event.qtd_tickets == null) return false;
    if (_seen.has(__event.id)) return false;
    _seen.add(__event.id);
    return true;
  });
}

/**
 * Lança/ajusta o Extra de Convites/Ingressos do formando num evento específico, fora do modal
 * grande de renegociação — mesmo espírito e mesmo mecanismo do "Adicionar Item Adicional": cada
 * tipo (Convite/Ingresso) é um `custom_payment` próprio (`item_adicional` + invite_type), e o
 * "Gerar Diferença" (aberto pela página via refreshCallback) cobra só o acréscimo, sem mexer nos
 * pagamentos já negociados.
 */
const ModalAdditionalInvite = props => {
  const { show, onHide, trainee, financial, refreshCallback } = props;
  const { config } = useWrapper();
  const { formatReal } = useFormat();
  const canCreate = usePermission("financialGroup", "create");
  const canUpdate = usePermission("financialGroup", "update");

  const [event_id, setevent_id] = useState(null);
  const [extraInvitations, setextraInvitations] = useState(0);
  const [extraTickets, setextraTickets] = useState(0);
  const [isWaitingResponse, setisWaitingResponse] = useState(false);

  const events = buildEventOptions(financial);
  const _selected_event = events.find(__event => __event.id === event_id);

  // Convite e Ingresso são dois Itens Adicionais independentes (um por invite_type) — cada um
  // com seu próprio id e quantidade, já que `quantity` guarda o Extra direto. Não filtra por
  // `disabled` de propósito: se um Extra foi zerado antes (soft-delete), precisa achar essa
  // mesma linha e reativá-la, em vez de criar uma duplicata — mesmo critério do
  // `buildEventInvites` em modal-financial-receipt.jsx.
  const _existing_invitation = financial?.event_invites?.find(
    __invite => __invite.event_id === event_id && __invite.invite_type === "convite",
  );
  const _existing_ticket = financial?.event_invites?.find(
    __invite => __invite.event_id === event_id && __invite.invite_type === "ingresso",
  );
  const _base_invitations = _selected_event?.qtd_invitations || 0;
  const _base_tickets = _selected_event?.qtd_tickets || 0;
  const _current_extra_invitations = _existing_invitation?.quantity || 0;
  const _current_extra_tickets = _existing_ticket?.quantity || 0;
  // Extra negativo (abriu mão de parte/todo o Base) só existe vindo da Renegociação completa —
  // esse atalho só sabe aumentar, então trava o campo em vez de arriscar mostrar o valor errado
  // (sem sinal) ou, pior, zerar o desconto sem o gestor perceber.
  const _invitations_locked = _current_extra_invitations < 0;
  const _tickets_locked = _current_extra_tickets < 0;

  useEffect(() => {
    if (show) {
      setevent_id(null);
      setextraInvitations(0);
      setextraTickets(0);
    }
  }, [show]);

  useEffect(() => {
    setextraInvitations(Math.max(0, _current_extra_invitations));
    setextraTickets(Math.max(0, _current_extra_tickets));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- só reage à troca de evento, não a toda mudança de estado derivado (senão sobrescreveria o que o usuário acabou de digitar)
  }, [event_id]);

  const closeModal = () => {
    if (isWaitingResponse) return;
    onHide();
  };

  // Campo travado (negativo) não participa da conta de aumento — o valor "atual" e o "novo" são
  // o mesmo (o próprio negativo), diferença zero, então esse lado nunca é tocado por este modal.
  const _current_value =
    _current_extra_invitations * (_selected_event?.value_invitation || 0) +
    _current_extra_tickets * (_selected_event?.value_ticket || 0);
  const _new_value =
    (_invitations_locked ? _current_extra_invitations : extraInvitations || 0) *
      (_selected_event?.value_invitation || 0) +
    (_tickets_locked ? _current_extra_tickets : extraTickets || 0) * (_selected_event?.value_ticket || 0);
  const total = _new_value - _current_value;

  const saveInvite = async () => {
    if (!canCreate) return;
    const _is_valid = validateForm(document.getElementById("additional_invite_container"));
    if (!_is_valid) return;
    if (!(total > 0.005)) {
      toast.error(
        "O Extra precisa aumentar em relação ao já negociado. Para reduzir, use Renegociar Financeiro.",
      );
      return;
    }
    if ((_existing_invitation?.id || _existing_ticket?.id) && !canUpdate) {
      toast.error("Você não tem permissão para atualizar essa negociação.");
      return;
    }

    setisWaitingResponse(true);
    try {
      const _rows = [
        ...(_invitations_locked
          ? []
          : [
              {
                id: _existing_invitation?.id,
                invite_type: "convite",
                quantity: extraInvitations || 0,
                value: _selected_event?.value_invitation || 0,
              },
            ]),
        ...(_tickets_locked
          ? []
          : [
              {
                id: _existing_ticket?.id,
                invite_type: "ingresso",
                quantity: extraTickets || 0,
                value: _selected_event?.value_ticket || 0,
              },
            ]),
      ].filter(__row => !!__row.id || __row.quantity > 0);

      for (const __row of _rows) {
        const _data = {
          payer_type: "item_adicional",
          trainees: [trainee?.id],
          commercial_id: trainee?.commercial_id,
          event_id,
          invite_type: __row.invite_type,
          name: __row.invite_type === "convite" ? "Convite Extra" : "Ingresso Extra",
          quantity: __row.quantity,
          value: __row.value,
          convenience_fee: 0,
          disabled: !(__row.quantity > 0),
        };
        __row.id
          ? await api.put(`custom-payment/${__row.id}`, _data, config)
          : await api.post("custom-payment", _data, config);
      }

      toast.success("Convites/Ingressos Extras atualizados!");
      onHide();
      refreshCallback && refreshCallback();
    } catch (__err) {
      const _message = __err?.response?.data?.error?.message;
      toast.error(_message || "Problemas ao atualizar Convites/Ingressos Extras!");
      console.error("__err", __err);
    } finally {
      setisWaitingResponse(false);
    }
  };

  return (
    <ModalCustom show={show} onHide={closeModal} id="additional_invite_container" size="lg" disableClose={isWaitingResponse}>
      <ModalCustom.Header>
        <ModalCustom.Title>Adicionar Convites/Ingressos Extras</ModalCustom.Title>
      </ModalCustom.Header>
      <ModalCustom.Body>
        <p className="small-label mb-2">
          O Extra entra no valor contratado do formando e <b>aumenta</b> o que ele deve, além da Base já inclusa no
          pacote/avulso. Ao salvar, você poderá gerar os boletos da diferença, sem interferir nos pagamentos já
          negociados.
        </p>
        <Row className="mt-2">
          <Col sm={12}>
            <span>
              Evento <Required />
            </span>
            <Slct
              required={true}
              isDisabled={isWaitingResponse}
              options={events.map(__event => ({ value: __event.id, label: __event.name }))}
              value={event_id}
              onChange={__ev => setevent_id(__ev.value)}
              placeholder="..."
            />
          </Col>
        </Row>
        {!!_selected_event && (
          <>
            <Row className="mt-2">
              <Col sm={6}>
                <span>Convites Extras (Base: {_base_invitations})</span>
                <IntegerNumber
                  disabled={isWaitingResponse || _invitations_locked}
                  allowNegative={false}
                  value={extraInvitations}
                  valueChange={__ev => setextraInvitations(Number(__ev) || 0)}
                  placeholder="0"
                />
                {_invitations_locked && (
                  <p className="small-label mb-0 mt-1">
                    Este evento tem um desconto de Base negociado (Extra negativo). Use Renegociar Financeiro pra
                    ajustar.
                  </p>
                )}
              </Col>
              <Col sm={6}>
                <span>Ingressos Extras (Base: {_base_tickets})</span>
                <IntegerNumber
                  disabled={isWaitingResponse || _tickets_locked}
                  allowNegative={false}
                  value={extraTickets}
                  valueChange={__ev => setextraTickets(Number(__ev) || 0)}
                  placeholder="0"
                />
                {_tickets_locked && (
                  <p className="small-label mb-0 mt-1">
                    Este evento tem um desconto de Base negociado (Extra negativo). Use Renegociar Financeiro pra
                    ajustar.
                  </p>
                )}
              </Col>
            </Row>
            <Row className="mt-3">
              <Col sm={6}></Col>
              <Col sm={6}>
                <Results
                  rows={[
                    {
                      label: "Extra já negociado",
                      value: formatReal(_current_value),
                      nowrap: true,
                    },
                    {
                      label: <b>Total a Acrescentar</b>,
                      value: <b>{formatReal(Math.max(0, total))}</b>,
                      type: "dark",
                      nowrap: true,
                    },
                  ]}
                />
              </Col>
            </Row>
          </>
        )}
      </ModalCustom.Body>
      <ModalCustom.Footer>
        <Button disabled={isWaitingResponse} color="primary_ghost" onClick={closeModal}>
          Cancelar
        </Button>
        <Button disabled={isWaitingResponse || !canCreate || !_selected_event} onClick={saveInvite} loading={isWaitingResponse}>
          Adicionar Extra
        </Button>
      </ModalCustom.Footer>
    </ModalCustom>
  );
};

export default ModalAdditionalInvite;
